import { useEffect } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $profileStore } from '@lib/store';
import { ValidateUtils } from '@shared/utils';
import { BaseInput, Button, ErrorMessage, Modal, PhoneInput } from '@shared/widgets';
import { Constants } from '@shared/constants';

interface IForm {
  email: string;
  phone: { countryCode: string; phoneNumber: string };
}
const DEFAULT_VALUES: IForm = { email: '', phone: { countryCode: Constants.COUNTRY_CODE, phoneNumber: '' } };

interface IEditDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EditDetailsModal = (props: IEditDetailsModalProps) => {
  const profileStore = useUnit($profileStore.store);

  const { control, formState, handleSubmit, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('profile');

  useEffect(() => {
    reset({
      ...DEFAULT_VALUES,
      email: profileStore.data?.emailAddress || '',
      phone: {
        ...DEFAULT_VALUES.phone,
        countryCode: profileStore.data?.phoneAreaCode || Constants.COUNTRY_CODE,
        phoneNumber: profileStore.data?.phoneNumber || '',
      },
    });
  }, [profileStore.data]);

  const closeHandler = () => {
    reset({
      ...DEFAULT_VALUES,
      email: profileStore.data?.emailAddress || '',
      phone: {
        ...DEFAULT_VALUES.phone,
        countryCode: profileStore.data?.phoneAreaCode || Constants.COUNTRY_CODE,
        phoneNumber: profileStore.data?.phoneNumber || '',
      },
    });
    props.onClose();
  };
  const submitHandler = async (data: IForm) => {
    if (!profileStore.data) return;
    await $profileStore.events.updateProfile({
      ...profileStore.data,
      id: profileStore.data.id,
      emailAddress: data.email,
      phoneAreaCode: data.phone.countryCode,
      phoneNumber: data.phone.phoneNumber,
    });
    closeHandler();
  };

  return (
    <Modal title={t('corporateOptions.modal.title')} isOpen={props.isOpen} onClose={closeHandler}>
      <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='email'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <BaseInput {...field} placeholder={t('placeholders.email', { ns: 'common' })} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Controller
          name='phone'
          control={control}
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            validate: (value) => {
              if (!value?.countryCode) return t('errors.form.required', { ns: 'common' });
              if (!value?.phoneNumber) return t('errors.form.required', { ns: 'common' });
              if (!ValidateUtils.validatePhoneNumber(value.phoneNumber)) return t('errors.form.phone.invalidFormat', { ns: 'common' });
              return true;
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <div>
              <PhoneInput enableSearch value={field.value} onChange={field.onChange} required />
              <ErrorMessage text={error?.message} />
            </div>
          )}
        />
        <Button className='mt-7' color='primary' fullWidth type='submit' disabled={formState.isSubmitting}>
          {t('corporateOptions.modal.buttons.saveOptions')}
        </Button>
      </form>
    </Modal>
  );
};
